import { useState, useEffect, useRef } from "react";
import JobDetailsModal from "./add-job-modal-comps/JobDetailsModal";
import { JobDescriptionModal } from "./add-job-modal-comps/JobDescriptionModal";
import { JobExperienceModal } from "./add-job-modal-comps/JobExperienceModal";
import { ZohoCredentialsModal } from "./add-job-modal-comps/ZohoCredentialsModal";
import { AtsJobPostingModal } from "./add-job-modal-comps/AtsJobPostingModal";
import { SkillsModal } from "./add-job-modal-comps/SkillsModal";
import { BASE_URL } from "../../Constants";
import api from "../login/interceptors";
import cn from "../../utils/cn";
import { ArrowLeftIcon, ArrowRightIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { Portal } from "../Portals";
import { Button, useTheme } from "@mui/material";
import useFocusTrap from "../../hooks/useFocusTrap";
import LimitReachedModal from "./candidate/LimitReachedModal";
import { resumeLimitMessage } from "../../utils/organisationApi";

function AddJobModal({
  setIsAddJobOpen,
  isAddJobOpen,
  jobDescriptionMain,
  jobTitleMain,
  skillsMain,
  jobExperienceMain,
  mandatorySkillsMain,
  preferredSkillsMain,
  aliasSkillsMain,
  atsName,
  isEdit,
  atsId,
  jd_uuid,
  totalJobs
}) {
  const [step, setStep] = useState(1);
  const [isLimitReachedModalOpen, setIsLimitReachedModalOpen] = useState(false);
  const [resumeLimitNotice, setResumeLimitNotice] = useState("");
  const [jobTitle, setJobTitle] = useState(jobTitleMain || "");
  const [selectedATS, setSelectedATS] = useState((atsName && atsName === 'internal') ? 'none' : atsName);
  const [selectedATSTitle, setSelectedATSTitle] = useState('');
  const [selectedATSID, setSelectedATSID] = useState(atsId || '');
  const [jobDescription, setJobDescription] = useState(jobDescriptionMain || "");
  const [jobExperience, setJobExperience] = useState(jobExperienceMain || 0);
  const [isJobExperienceChanged, setIsJobExperienceChanged] = useState(false);
  const [skills, setSkills] = useState(skillsMain || []);
  const [atsRequiredSkills, setAtsRequiredSkills] = useState([]);
  const [mandatorySkills, setMandatorySkills] = useState(mandatorySkillsMain || []);
  const [preferredSkills, setPreferredSkills] = useState(preferredSkillsMain || []);
  const [suggestedSkills, setSuggestedSkills] = useState([])
  const [aliasSkills, setAliasSkills] = useState(aliasSkillsMain || {});
  const [isDuplicateSkill, setIsDuplicateSkill] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [newSkills, setNewSkills] = useState([]);
  const [zohoAuthenticated, setZohoAuthenticated] = useState(false);

  const initialMandatorySkills = useRef(mandatorySkillsMain || []);
  const initialPreferredSkills = useRef(preferredSkillsMain || []);

  const [zohoClientId, setZohoClientId] = useState('');
  const [zohoClientSecret, setZohoClientSecret] = useState('');

  const [jobOpenings, setJobOpenings] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [noJobPostingsFound, setNoJobPostingsFound] = useState(false);

  const theme = useTheme();

  const stepComponents = {
    1: <JobDetailsModal jobTitle={jobTitle} setJobTitle={setJobTitle} selectedATS={selectedATS} setSelectedATS={setSelectedATS} isEdit={isEdit} />,
    2: selectedATS === "zoho" ? (
      <ZohoCredentialsModal
        zohoClientId={zohoClientId}
        setZohoClientId={setZohoClientId}
        zohoClientSecret={zohoClientSecret}
        setZohoClientSecret={setZohoClientSecret}
        errorMessage={errorMessage}
      />) : null,
    3: selectedATS === "zoho" ? (
      <AtsJobPostingModal
        jobOpenings={jobOpenings}
        setJobOpenings={setJobOpenings}
        selectedJob={selectedJob}
        setSelectedJob={setSelectedJob}
        errorMessage={errorMessage}
        noJobPostingsFound={noJobPostingsFound}
        setNoJobPostingsFound={setNoJobPostingsFound}
        setJobDescription={setJobDescription}
        setSelectedATSTitle={setSelectedATSTitle}
        setSelectedATSID={setSelectedATSID}
        setJobExperience={setJobExperience}
        setAtsRequiredSkills={setAtsRequiredSkills}
        atsRequiredSkills={atsRequiredSkills}
      />) : null,
    4: <JobDescriptionModal jobDescription={jobDescription} setJobDescription={setJobDescription} setIsJobExperienceChanged={setIsJobExperienceChanged} />,
    5: <SkillsModal
      skills={skills}
      setSkills={setSkills}
      mandatorySkills={mandatorySkills}
      setMandatorySkills={setMandatorySkills}
      preferredSkills={preferredSkills}
      setPreferredSkills={setPreferredSkills}
      aliasSkills={aliasSkills}
      setAliasSkills={setAliasSkills}
      setIsDuplicateSkill={setIsDuplicateSkill}
      isDuplicateSkill={isDuplicateSkill}
      newSkills={newSkills}
      setNewSkills={setNewSkills}
      suggestedSkills={suggestedSkills}
    />,
    6: <JobExperienceModal jobExperience={jobExperience} setJobExperience={setJobExperience} />,
  };

  const getTotalStepCount = () => Object.keys(stepComponents).length;

  const ifCurrentStepEquals = (stepValue) => {
    return step === stepValue;
  }

  const fetchResultsWithDescription = async (description) => {
    try {
      setIsLoading(true);
      setErrorMessage("");
      if (description.trim() === "") {
        setErrorMessage("Job description is empty. Please provide a valid job description.")
        return;
      }

      const response = await api.post(`${BASE_URL}/api/parse_job_description/`, {
        job_description: description,
        jd_uuid: isEdit ? jd_uuid : "",
      }, {
        headers: {
          "Content-Type": "application/json",
        }
      });

      const fetchedData = response.data;
      if (fetchedData) {
        if (isJobExperienceChanged) {
          setJobExperience(fetchedData?.experience ?? 0);
        }
        else {
          setJobExperience(jobExperience || (fetchedData?.experience ?? 0) || 0);
        }

        setSkills((prevSkills) => {
          const presistSkills = [...prevSkills].filter(
            (skill) => mandatorySkills?.includes(skill) || preferredSkills?.includes(skill)
          );

          const atsSkills = atsName !== 'internal' ? atsRequiredSkills : [];
          const matchedSkills = fetchedData.matched_skills || [];

          const skillMap = new Map();

          [...presistSkills, ...matchedSkills].forEach(skill => {
            skillMap.set(skill.toLowerCase(), skill);
          });

          atsSkills.forEach(skill => {
            const lower = skill.toLowerCase();
            if (!skillMap.has(lower)) {
              skillMap.set(lower, skill);
            }
          });

          return Array.from(skillMap.values());
        })
        setSuggestedSkills(fetchedData.suggested_skills)
      }

    } catch (error) {
      console.error("Error fetching skills and experience with description:", error);
      setErrorMessage("Error fetching skills and experience with description")
    } finally {
      setIsLoading(false)
    }
  };

  const handleZohoAuthentication = async () => {
    const clientId = zohoClientId;
    const redirectUri = process.env.REACT_APP_ZOHO_REDIRECT_URI

    const scope = 'ZohoRecruit.modules.READ';
    const responseType = 'code';

    const authUrl = `https://accounts.zoho.com/oauth/v2/auth?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${encodeURIComponent(scope)}&response_type=${responseType}&access_type=offline&prompt=consent`;

    const width = 500, height = 550;
    const top = (window.innerHeight - height) / 2;
    const left = (window.innerWidth - width) / 2;

    const popup = window.open(
      authUrl,
      'ZohoLoginPopup',
      `width=${width},height=${height},top=${top},left=${left},resizable=yes,scrollbars=yes,toolbar=no,status=no,menubar=no`
    );

    const checkPopup = setInterval(() => {
      if (!popup || popup.closed) {
        clearInterval(checkPopup);
      }
    }, 1000);

    const handleMessage = async (event) => {
      if (event.origin !== window.location.origin) return;

      const { code, error } = event.data;
      window.removeEventListener('message', handleMessage);

      if (error === "access_denied") {
        setErrorMessage("Zoho access denied.");
        popup?.close();

        setTimeout(() => setErrorMessage(""), 3000);
        return;
      }
      if (!code) return;

      try {
        setZohoAuthenticated(true);
        const response = await api.post(`${BASE_URL}/api/auth/zoho/token`,
          { code, redirect_uri: redirectUri, zoho_client_id: zohoClientId, zoho_secret: zohoClientSecret }
        );

        if (response.status === 200) {
          localStorage.setItem('zoho_access_token', response.data.access_token);
          localStorage.setItem('zoho_refresh_token', response.data.refresh_token);
          try {
            await fetchZohoJobOpenings();
            setStep(step + 1);
          } catch (fetchError) {
            console.error("Error fetching job openings from Zoho:", fetchError);
            setErrorMessage("Failed to fetch job openings from Zoho.");
            setTimeout(() => setErrorMessage(""), 5000);
          }
        }
      } catch (error) {
        console.error('Error exchanging authorization code:', error);
        setErrorMessage("Zoho authentication failed!");
        setTimeout(() => setErrorMessage(""), 3000);
      } finally {
        setZohoAuthenticated(false);
        popup?.close();
      }
    };

    window.addEventListener('message', handleMessage, { once: true });
  };

  const fetchZohoJobOpenings = async () => {
    try {
      const jobOpeningsResponse = await api.post(
        `${BASE_URL}/api/zoho/job_openings`,
        { zoho_token: localStorage.getItem("zoho_access_token") }
      );

      if (jobOpeningsResponse.status === 200) {
        const jobData = jobOpeningsResponse.data?.data || [];
        if (jobData.length === 0) {
          setNoJobPostingsFound(true);
        } else {
          const existingIds = new Set(totalJobs.map(job => job.ats_ref_id));
          const uniqueJobs = jobData.filter(job => !existingIds.has(job.id));
          const mergedJobs = [...uniqueJobs];
          setJobOpenings(mergedJobs);
          setNoJobPostingsFound(false);
          setStep(step + 1);
        }
      } else {
        console.error("Error fetching job openings from Zoho:");
      }
    } catch (error) {
      console.error("Error fetching job openings from Zoho:", error);
    }
  };

  const handleModal = () => {
    setIsAddJobOpen(false);
  };

  const handleNext = async () => {
    setIsLoading(true)
    if (selectedATS === "none") {
      if (step === 1) {
        setStep(4);
        setIsLoading(false)
        return;
      }
    }

    if (selectedATS === 'zoho') {
      if (ifCurrentStepEquals(1)) {
        if (atsId) {
          setStep(4);
          setIsLoading(false)
          return;
        }
      }
    }

    if (ifCurrentStepEquals(2)) {
      if (selectedATS === "zoho") {
        if (!zohoClientId || !zohoClientSecret) {
          setErrorMessage("Please enter both Client ID and Client Secret.");
          setIsLoading(false)
          return;
        }
        await handleZohoAuthentication();
        setIsLoading(false)
        return;
      }
    }


    if (ifCurrentStepEquals(4)) {
      await fetchResultsWithDescription(jobDescription)
    }
    if (ifCurrentStepEquals(5)) {
      handleConfirmSkills();
    }
    if (step < getTotalStepCount()) {
      setStep(step + 1);
    }
    setIsLoading(false)
  };

  const handleConfirmSkills = () => {
    const confirmedSkills = newSkills.filter((s) => s.name.trim() !== "");

    setSkills((prev) => [...prev, ...confirmedSkills.map((s) => s.name)]);

    setMandatorySkills((prev) => [
      ...prev,
      ...confirmedSkills.filter((s) => s.type === "mandatory").map((s) => s.name)
    ]);

    setPreferredSkills((prev) => [
      ...prev,
      ...confirmedSkills.filter((s) => s.type === "preferred").map((s) => s.name)
    ]);

    setNewSkills([]);

  };

  const handleBack = () => {
    if (selectedATS === "none") {
      if (step === 4) {
        setStep(1);
        return;
      }
    }

    if (selectedATS === 'zoho') {
      if (ifCurrentStepEquals(4)) {
        if (atsId) {
          setStep(1);
          return;
        }
      }
    }

    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleSubmit = async () => {
    await submitEvaluation();
  };

  const submitEvaluation = async () => {
    setIsLoading(true);
    const allSelectedSkills = new Set([...mandatorySkills, ...preferredSkills]);

    const filteredAliasSkills = Object.fromEntries(
      Object.entries(aliasSkills).filter(([skill]) => allSelectedSkills.has(skill))
    );

    try {
      if (isEdit) {
        const updatePayload = {
          job_description: jobDescription,
          job_title: jobTitle,
          ats_id: selectedATSID || "",
          provider: selectedATS === 'none' ? 'internal' : selectedATS,
          ats_title: selectedATSTitle || "",
          parsed_skills: skills || [],
          mandatory_skills: [...new Set(mandatorySkills)] || [],
          preferred_skills: [...new Set(preferredSkills)] || [],
          skill_aliases: filteredAliasSkills || {},
          min_exp: jobExperience || 0,
        };

        const response = await api.put(
          `${BASE_URL}/api/resume-parser/${jd_uuid}`,
          updatePayload
        );

        if (response.status === 200) {
          window.location.reload();
        }
      } else {
        const formData = new FormData();

        formData.append("files[]", []);
        formData.append("jd_uuid", "");
        formData.append("job_description", jobDescription);
        formData.append("job_title", jobTitle);
        formData.append("ats_id", selectedATSID || "");
        formData.append("provider", selectedATS === 'none' ? 'internal' : selectedATS);
        formData.append("ats_title", selectedATSTitle || "");
        formData.append("parsed_skills", skills || []);
        formData.append("mandatory_skills", [...new Set(mandatorySkills)] || []);
        formData.append("preferred_skills", [...new Set(preferredSkills)] || []);
        formData.append("skill_aliases", JSON.stringify(filteredAliasSkills) || {});
        formData.append("min_exp", jobExperience || 0);
        formData.append("flag_ref", 'add');
        formData.append("is_open_ai", 'true');

        const response = await api.post(
          `${BASE_URL}/api/resume-parser/`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        if (response.status === 200) {
          window.location.reload();
        }
      }
    } catch (error) {
      const limitNotice = resumeLimitMessage(error);
      if (limitNotice) {
        setResumeLimitNotice(limitNotice);
        setIsLimitReachedModalOpen(true);
        setIsLoading(false);
        return;
      }
      if (error.response) {
        const errorMessage = error.response.data?.error || "Something went wrong.";
        if (error.response.status === 413) {
          setErrorMessage(errorMessage);
        } else if (error.response.status === 500) {
          setErrorMessage("Something went wrong with the server. Please try again later.");
        } else {
          setErrorMessage(errorMessage);
        }

        setTimeout(() => {
          setErrorMessage("");
        }, 10000);
      } else {
        console.error("Error uploading resumes:", error);
      }
    } finally {
      setIsLoading(false);
    }
  };


  const getAddJobFormStatus = () => {
    if (ifCurrentStepEquals(2)) {
      return (
        isLoading ||
        zohoAuthenticated ||
        (selectedATS === "zoho" && (!zohoClientId || !zohoClientSecret)) ||
        (selectedATS !== "zoho" && !jobDescription)
      );
    }

    if (ifCurrentStepEquals(3)) {
      return (
        isLoading ||
        noJobPostingsFound ||
        !selectedJob
      );
    }

    return (
      isLoading ||
      (ifCurrentStepEquals(1) && (!jobTitle || !selectedATS)) ||
      (ifCurrentStepEquals(2) && !jobDescription) ||
      (ifCurrentStepEquals(3) && (newSkills.some(skill => skill.name.trim() === ""))) ||
      (ifCurrentStepEquals(4) && !jobDescription) ||
      (ifCurrentStepEquals(5) && (
        (newSkills.some(skill => skill.name.trim() === "")) ||
        (mandatorySkills?.length === 0 && preferredSkills?.length === 0) ||
        (isDuplicateSkill)
      ))
    )
  }

  useEffect(() => {
    if (ifCurrentStepEquals(4)) {
      setMandatorySkills(initialMandatorySkills.current);
      setPreferredSkills(initialPreferredSkills.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  useEffect(() => {
    const closeButton = document.querySelector('[aria-label="Close pop-up"]');
    if (closeButton) {
      closeButton.focus();
    }
  }, [step]);

  const modalRef = useFocusTrap(isAddJobOpen, handleModal);
  return (
    <>
    <Portal>
      <div className="fixed inset-0 flex items-start justify-center z-50 bg-black bg-opacity-70">
        <div
          ref={modalRef}
          className="rounded-lg w-full max-w-47rem overflow-hidden relative flex flex-col justify-between max-sm:mx-2 my-auto"
          style={{
            maxHeight: "95vh",
            overflow: "auto",
            backgroundColor: theme.palette.primary.main
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
        >
          {zohoAuthenticated && (
            <div className="absolute inset-0 flex justify-center items-center bg-black bg-opacity-60 z-50">
              <div className="flex flex-col items-center">
                <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-blue-500"></div>
                <p className="mt-2 text-white">Authentication successful! Retrieving job listings...</p>
              </div>
            </div>
          )}

          {errorMessage && (
            <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-red-500 text-white text-sm px-4 py-2 rounded-md shadow-md z-50">
              {errorMessage}
            </div>
          )}

          <div className="w-full flex justify-between border-b p-4 px-5" style={{ borderColor: theme.palette.secondary.v3 }}>
            <h2 id="modal-heading"
              className="font-semibold">{!isEdit ? "Add Job" : "Edit Job"}
            </h2>
            <button
              onClick={handleModal}
              aria-label="Close pop-up"
              tabIndex={0}
              className="bg-black text-white rounded-full w-6 h-6 flex items-center justify-center"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>

          <div className="flex-1 overflow-auto">
            {stepComponents[step]}
          </div>

          <div className="flex justify-between border-t p-3 px-5" style={{ borderColor: theme.palette.secondary.v3 }}>
            <Button
              onClick={handleBack}
              disabled={ifCurrentStepEquals(1)}
              sx={{
                color: theme.palette.text.main,
                textTransform: 'none',
                padding: "0",
                '&.Mui-disabled': {
                  backgroundColor: theme.palette.primary.main
                },
                '&:hover': {
                  backgroundColor: theme.palette.primary.main
                }
              }}
              className={cn('text-sm font-semibold', ifCurrentStepEquals(1) && "cursor-not-allowed")}
            >
              <span className="flex gap-1 items-center justify-center">
                <ArrowLeftIcon className="w-4 h-4 " />Back
              </span>
            </Button>

            <Button
              onClick={ifCurrentStepEquals(getTotalStepCount()) ? handleSubmit : handleNext}
              disabled={getAddJobFormStatus()}
              sx={
                {
                  backgroundColor: theme.palette.info.main,
                  color: theme.palette.text.light,
                  padding: "0 10px"
                }
              }
              className={
                cn(
                  getAddJobFormStatus() && "bg-gray-500 cursor-not-allowed"
                )}
            >
              {isLoading ? (
                <div className="flex gap-2 items-center justify-center text-black">
                  <p>{ifCurrentStepEquals(getTotalStepCount()) ? "Submit" : "Next"}</p>
                  <div className="animate-spin rounded-full border-t-2 border-black h-4 w-4"></div>
                </div>
              ) : (
                ifCurrentStepEquals(getTotalStepCount()) ?
                  <span className="flex gap-1 items-center justify-center">
                    Submit <ArrowRightIcon className="w-4 h-4 " />
                  </span> :
                  <span className="flex gap-1 items-center justify-center">
                    Next <ArrowRightIcon className="w-4 h-4 " />
                  </span>

              )}
            </Button>
          </div>
        </div>
      </div>
    </Portal>
    <LimitReachedModal
      isLimitReachedModalOpen={isLimitReachedModalOpen}
      setIsLimitReachedModalOpen={setIsLimitReachedModalOpen}
      message={resumeLimitNotice}
    />
    </>
  )
}

export default AddJobModal;
